/**
 * Die **Wallbox** (HM-110, ADR 0032): ein Ladepunkt für ein Elektroauto, gelesen
 * und gesteuert über das HausMeter Gateway, das ihn bei EVCC abholt.
 *
 * **Sie ist ein Knoten wie jeder andere** — ein Gerät hinter dem Gateway mit
 * eigenem Datenpunkt-Katalog, eigener Telemetrie, eigenem Zähler, sobald ein
 * Mensch den Anlagevorschlag bestätigt hat. Was sie von einem Shelly
 * unterscheidet, steht in zwei Sätzen:
 *
 * 1. **Sie ist das erste Gerät, an dem die Cloud etwas setzt.** Lademodus,
 *    Stromgrenzen, Phasen und Ladeziel sind Datenpunkte mit `access:
 *    "read_write"`; ein Schreibauftrag geht als `dp` an das Gateway, das ihn
 *    bei EVCC ausführt und quittiert (ADR 0019, ADR 0020).
 * 2. **Ihre Datenpunkte haben eine verabredete Bedeutung.** Ein Shelly meldet,
 *    was er misst, und die Cloud deutet nur die Kennzahl. Eine Wallbox meldet
 *    einen Lademodus, und den gibt es in keiner OBIS-Tabelle. Die Schlüssel
 *    unten sind deshalb ein **Vertrag** mit dem Gateway — wie `deviceName` in
 *    `settings`: Was so heißt, bedeutet das, und was anders heißt, wird
 *    angezeigt und nicht gedeutet (Regel 9).
 *
 * **Gedeutet wird in der API beim Abbilden**, nicht in der Oberfläche
 * (`wallboxStateOf`) — sonst hätte jede Anwendung ihre eigene Tabelle, und
 * Mobile läse einen Lademodus anders als Web. Die Oberfläche bekommt einen
 * fertigen Zustand samt der Datenpunkte, an die sie schreiben darf.
 *
 * Hier stehen die Regeln dazu — ohne Datenbank, ohne Uhr, ohne I/O.
 */
import type { RegisterRole } from "../meter/meter-register.js";
import type { Unit } from "../common/units.js";
import { isWritable, type DatapointAccess } from "./datapoint.js";

/**
 * Der Gerätetyp, für den dieser Vertrag gilt: ein **Ladepunkt aus EVCC**.
 *
 * Nicht „Wallbox" — der Name der Ansicht bleibt es, der Schlüssel nicht. Die
 * Lademodi und Datenpunkte unten sind die von EVCC; eine Wallbox aus einer
 * anderen Quelle bekäme einen eigenen Schlüssel und eine eigene Deutung, statt
 * unter diesem mit falschen Modi dazustehen (ADR 0032, Nachtrag).
 */
export const EVCC_LOADPOINT_DEVICE_TYPE = "evcc_loadpoint";

export function isEvccLoadpointDeviceType(deviceType: string): boolean {
  return deviceType === EVCC_LOADPOINT_DEVICE_TYPE;
}

/**
 * Der Lademodus, wie EVCC ihn führt — und wie er **als Zahl** auf der Leitung
 * steht.
 *
 * Die Telemetrie ist Schlüssel → Zahl (HM-047), und ein Schreibauftrag trägt
 * eine Zahl (`dp`). Ein Modus ist aber ein Wort. Die Kodierung steht deshalb
 * **einmal** hier, und Gateway wie Oberfläche lesen sie von derselben Stelle:
 * `0` aus, `1` nur PV-Überschuss, `2` Mindestladung plus PV, `3` sofort.
 *
 * Eine unbekannte Zahl ist **kein Modus** und wird `null` — ein Gateway mit
 * einer neueren Tabelle soll hier auffallen und nicht als „aus" erscheinen.
 */
export const WALLBOX_CHARGE_MODES = ["off", "pv", "minpv", "now"] as const;
export type WallboxChargeMode = (typeof WALLBOX_CHARGE_MODES)[number];

export const WALLBOX_CHARGE_MODE_CODES: Readonly<Record<WallboxChargeMode, number>> = {
  off: 0,
  pv: 1,
  minpv: 2,
  now: 3,
};

export function wallboxChargeModeOf(code: number): WallboxChargeMode | null {
  return WALLBOX_CHARGE_MODES.find((mode) => WALLBOX_CHARGE_MODE_CODES[mode] === code) ?? null;
}

/**
 * Was am Ladepunkt gerade los ist, nach IEC 61851: kein Fahrzeug (A),
 * angesteckt (B), lädt (C). Als Zahl `0`, `1`, `2` auf der Leitung.
 */
export const WALLBOX_STATUSES = ["disconnected", "connected", "charging"] as const;
export type WallboxStatus = (typeof WALLBOX_STATUSES)[number];

export const WALLBOX_STATUS_CODES: Readonly<Record<WallboxStatus, number>> = {
  disconnected: 0,
  connected: 1,
  charging: 2,
};

export function wallboxStatusOf(code: number): WallboxStatus | null {
  return WALLBOX_STATUSES.find((status) => WALLBOX_STATUS_CODES[status] === code) ?? null;
}

/**
 * Die Phasenzahl, die sich setzen lässt: `0` heißt „EVCC wählt selbst".
 * Ein anderer Wert wird abgelehnt, nicht gerundet.
 */
export const WALLBOX_PHASE_CHOICES = [0, 1, 3] as const;

/**
 * Eine Codetabelle: Wort → Zahl auf der Leitung, und zurück nur für Zahlen,
 * die in der Tabelle stehen. Eine unbekannte Zahl ist **kein** Wert und wird
 * `null` — nicht der nächstbeste (Regel 9).
 */
function decoderOf<T extends string>(
  words: readonly T[],
  codes: Readonly<Record<T, number>>,
): (code: number) => T | null {
  return (code) => words.find((word) => codes[word] === code) ?? null;
}

/**
 * **Immer mit Mindestleistung** (HM-165, evcc ≥ 0.316): Seit 0.316 kennt evcc
 * nur noch `off`, `smart` und `now`; `pv` und `minpv` sind `smart` mit
 * `alwaysCharge` aus bzw. an. Das Gateway meldet den Lademodus weiter mit den
 * vier Codes oben und daneben diese Einstellung. `once` heißt „an bis zum
 * Abstecken" — das Wort ist das von evcc.
 */
export const WALLBOX_ALWAYS_CHARGE_MODES = ["off", "on", "once"] as const;
export type WallboxAlwaysCharge = (typeof WALLBOX_ALWAYS_CHARGE_MODES)[number];
export const WALLBOX_ALWAYS_CHARGE_CODES: Readonly<Record<WallboxAlwaysCharge, number>> = {
  off: 0,
  on: 1,
  once: 2,
};
export const wallboxAlwaysChargeOf = decoderOf(
  WALLBOX_ALWAYS_CHARGE_MODES,
  WALLBOX_ALWAYS_CHARGE_CODES,
);

/** Worauf die Wallbox wartet (`charger-status-reason`): nichts, eine Freigabe (RFID), das Abstecken. */
export const WALLBOX_WAITING_REASONS = ["none", "authorization", "disconnect_required"] as const;
export type WallboxWaitingReason = (typeof WALLBOX_WAITING_REASONS)[number];
export const WALLBOX_WAITING_REASON_CODES: Readonly<Record<WallboxWaitingReason, number>> = {
  none: 0,
  authorization: 1,
  disconnect_required: 2,
};
export const wallboxWaitingReasonOf = decoderOf(
  WALLBOX_WAITING_REASONS,
  WALLBOX_WAITING_REASON_CODES,
);

/** Eine anstehende PV-Schaltung (`pv-action`): keine, einschalten, abschalten. */
export const WALLBOX_PV_ACTIONS = ["none", "enable", "disable"] as const;
export type WallboxPvAction = (typeof WALLBOX_PV_ACTIONS)[number];
export const WALLBOX_PV_ACTION_CODES: Readonly<Record<WallboxPvAction, number>> = {
  none: 0,
  enable: 1,
  disable: 2,
};
export const wallboxPvActionOf = decoderOf(WALLBOX_PV_ACTIONS, WALLBOX_PV_ACTION_CODES);

/** Eine anstehende Phasenumschaltung (`phase-action`): keine, auf eine, auf drei Phasen. */
export const WALLBOX_PHASE_ACTIONS = ["none", "scale_1p", "scale_3p"] as const;
export type WallboxPhaseAction = (typeof WALLBOX_PHASE_ACTIONS)[number];
export const WALLBOX_PHASE_ACTION_CODES: Readonly<Record<WallboxPhaseAction, number>> = {
  none: 0,
  scale_1p: 1,
  scale_3p: 2,
};
export const wallboxPhaseActionOf = decoderOf(WALLBOX_PHASE_ACTIONS, WALLBOX_PHASE_ACTION_CODES);

/**
 * Die Datenpunkte, die eine Wallbox meldet — der Vertrag mit dem Gateway.
 *
 * `role` steht an den Datenpunkten, die ein Zählwerk speisen können: Der
 * Gesamtzähler der Wallbox (`energy-import`) ist ihr Bezug, und mit ihm wird
 * aus der Wallbox ein Zähler, der in einen Steckplatz des Messkonzepts kommt
 * (HM-058, HM-110). Ströme und Spannungen je Phase (HM-165) hängen sich als
 * weitere Zählwerke an **denselben** Zähler — sie haben keinen Kanal und landen
 * in derselben Gruppe wie der Gesamtzähler; ohne ihn entsteht aus ihnen keiner
 * („ohne Energiezählwerk kein Zähler"). Die Sitzungsenergie hat **keine**
 * Rolle: Sie springt bei jedem Ladevorgang auf null zurück, und ein Verbrauch
 * daraus verstieße gegen die Monotonie, die ein Zählwerk voraussetzt (Regel 4).
 *
 * `unit` steht als Einheit dieses Produkts und nicht als DLMS-Code: Für ein
 * Verhältnis und eine Anzahl führt die Norm keinen Code, den ein Zähler je
 * gesendet hätte (HM-051). Der Katalog trägt dafür seit HM-110 das Feld
 * `unit`. **Ein Betrag oder Preis wird nur in der verabredeten Währung
 * gedeutet** (`EUR`, `EUR/kWh`); meldet das Gateway eine andere, ist der Wert
 * ein Datenpunkt ohne Einheit und erscheint hier nicht (Regel 9).
 *
 * **Drei Verabredungen aus HM-165** gelten für die neuen Schlüssel: `0` heißt
 * „nicht gesetzt", wo evcc kein Ziel, keinen Plan und keine Grenze hat — das
 * Gateway sendet die `0`, statt den Wert wegzulassen, damit der letzte Wert
 * nicht stehen bleibt. Wo `0` ein echter Wert wäre (eine Preisgrenze von
 * 0 €/kWh), sagt ein eigener Schalter `…-set`, ob die Grenze gilt. Ein
 * Zeitpunkt ist Unix-Sekunden UTC ohne Einheit, eine Dauer trägt `s`, ein
 * Schalter ist `0`/`1`.
 */
export interface WallboxDatapointSpec {
  readonly key: string;
  readonly name: string;
  readonly unit: Unit | null;
  readonly access: DatapointAccess;
  readonly role: RegisterRole | null;
}

const read = (
  key: string,
  name: string,
  unit: Unit | null,
  role: RegisterRole | null = null,
): WallboxDatapointSpec => ({ key, name, unit, access: "read", role });
const readWrite = (key: string, name: string, unit: Unit | null): WallboxDatapointSpec => ({
  key,
  name,
  unit,
  access: "read_write",
  role: null,
});

export const WALLBOX_DATAPOINTS = {
  chargePower: read("charge-power", "Ladeleistung", "W", "power_total"),
  energyImport: read("energy-import", "Geladen gesamt", "Wh", "energy_import_total"),
  sessionEnergy: read("session-energy", "Geladen in dieser Sitzung", "Wh"),
  status: read("status", "Ladezustand", "count"),
  vehicleSoc: read("vehicle-soc", "Ladestand des Fahrzeugs", "ratio"),
  chargeMode: readWrite("charge-mode", "Lademodus", null),
  minCurrent: readWrite("min-current", "Mindeststrom", "A"),
  maxCurrent: readWrite("max-current", "Maximalstrom", "A"),
  phases: readWrite("phases", "Phasen", "count"),
  limitSoc: readWrite("limit-soc", "Ladeziel", "ratio"),

  /* HM-165: was evcc am Ladepunkt sonst weiß und kann. */
  currentL1: read("current-l1", "Ladestrom L1", "A", "current_l1"),
  currentL2: read("current-l2", "Ladestrom L2", "A", "current_l2"),
  currentL3: read("current-l3", "Ladestrom L3", "A", "current_l3"),
  voltageL1: read("voltage-l1", "Spannung L1", "V", "voltage_l1"),
  voltageL2: read("voltage-l2", "Spannung L2", "V", "voltage_l2"),
  voltageL3: read("voltage-l3", "Spannung L3", "V", "voltage_l3"),
  phasesActive: read("phases-active", "Aktive Phasen", "count"),
  offeredCurrent: read("offered-current", "Angebotener Strom", "A"),
  enabled: read("enabled", "Ladung freigegeben", null),
  chargerStatusReason: read("charger-status-reason", "Wartet auf", null),
  chargeDuration: read("charge-duration", "Ladedauer der Sitzung", "s"),
  connectedDuration: read("connected-duration", "Angesteckt seit", "s"),
  chargeRemainingDuration: read("charge-remaining-duration", "Restladezeit", "s"),
  chargeRemainingEnergy: read("charge-remaining-energy", "Restenergie", "Wh"),
  sessionSolarShare: read("session-solar-share", "Solaranteil der Sitzung", "ratio"),
  sessionPrice: read("session-price", "Kosten der Sitzung", "EUR"),
  sessionPricePerKwh: read("session-price-per-kwh", "Preis je kWh der Sitzung", "EUR/kWh"),
  sessionCo2PerKwh: read("session-co2-per-kwh", "CO₂ je kWh der Sitzung", "g/kWh"),
  pvAction: read("pv-action", "PV-Schaltung steht an", null),
  pvRemaining: read("pv-remaining", "PV-Schaltung in", "s"),
  phaseAction: read("phase-action", "Phasenumschaltung steht an", null),
  phaseRemaining: read("phase-remaining", "Phasenumschaltung in", "s"),
  effectiveMinCurrent: read("effective-min-current", "Wirksamer Mindeststrom", "A"),
  effectiveMaxCurrent: read("effective-max-current", "Wirksamer Maximalstrom", "A"),
  effectiveLimitSoc: read("effective-limit-soc", "Wirksames Ladeziel", "ratio"),
  effectivePlanSoc: read("effective-plan-soc", "Ziel des wirksamen Plans", "ratio"),
  effectivePlanTime: read("effective-plan-time", "Zeit des wirksamen Plans", null),
  alwaysCharge: readWrite("always-charge", "Immer mit Mindestleistung", null),
  limitEnergy: readWrite("limit-energy", "Energieziel der Sitzung", "Wh"),
  priority: readWrite("priority", "Vorrang", "count"),
  batteryBoost: readWrite("battery-boost", "Hausakku-Boost", null),
  batteryBoostLimit: readWrite("battery-boost-limit", "Hausakku-Boost bis", "ratio"),
  enableThreshold: readWrite("enable-threshold", "Einschaltschwelle", "W"),
  disableThreshold: readWrite("disable-threshold", "Abschaltschwelle", "W"),
  enableDelay: readWrite("enable-delay", "Einschaltverzögerung", "s"),
  disableDelay: readWrite("disable-delay", "Abschaltverzögerung", "s"),
  /** Die Einheit folgt `smartCostType` der Anlage: `EUR/kWh` oder `g/kWh`. */
  smartCostLimit: readWrite("smart-cost-limit", "Günstig laden unter", "EUR/kWh"),
  smartCostLimitSet: readWrite("smart-cost-limit-set", "Günstig laden eingerichtet", null),
  smartCostActive: read("smart-cost-active", "Günstig laden aktiv", null),
  smartCostNextStart: read("smart-cost-next-start", "Günstig laden ab", null),
  smartFeedInPriorityLimit: readWrite(
    "smart-feed-in-priority-limit",
    "Einspeisevorrang über",
    "EUR/kWh",
  ),
  smartFeedInPriorityLimitSet: readWrite(
    "smart-feed-in-priority-limit-set",
    "Einspeisevorrang eingerichtet",
    null,
  ),
  smartFeedInPriorityActive: read("smart-feed-in-priority-active", "Einspeisevorrang aktiv", null),
  smartFeedInPriorityNextStart: read(
    "smart-feed-in-priority-next-start",
    "Einspeisevorrang ab",
    null,
  ),
  planEnergy: readWrite("plan-energy", "Energieplan: Ziel", "Wh"),
  planEnergyTime: readWrite("plan-energy-time", "Energieplan: fertig um", null),
  planSoc: readWrite("plan-soc", "Ladestandsplan: Ziel", "ratio"),
  planSocTime: readWrite("plan-soc-time", "Ladestandsplan: fertig um", null),
  planActive: read("plan-active", "Plan lädt gerade", null),
  planProjectedStart: read("plan-projected-start", "Plan beginnt", null),
  planProjectedEnd: read("plan-projected-end", "Plan endet", null),
  planOverrun: read("plan-overrun", "Plan verspätet sich um", "s"),
  vehicle: readWrite("vehicle", "Fahrzeug", null),
  vehicleRange: read("vehicle-range", "Reichweite", "km"),
  vehicleOdometer: read("vehicle-odometer", "Kilometerstand", "km"),
  vehicleApiLimitSoc: read("vehicle-api-limit-soc", "Ladegrenze im Fahrzeug", "ratio"),
  vehicleMinSoc: readWrite("vehicle-min-soc", "Mindestladestand des Fahrzeugs", "ratio"),
  vehicleLimitSoc: readWrite("vehicle-limit-soc", "Ladeziel des Fahrzeugs", "ratio"),
} as const satisfies Readonly<Record<string, WallboxDatapointSpec>>;

export type WallboxDatapointName = keyof typeof WALLBOX_DATAPOINTS;

/**
 * Die Namen der Fahrzeuge (HM-165): je Fahrzeug der Anlage ein
 * **Textdatenpunkt** `vehicle-{n}` (HM-052). Die Nummer vergibt das Gateway
 * einmal und für immer am Namen des Fahrzeugs in evcc; sie ist so stabil wie
 * ein Schlüssel, und eine gelöschte wird nie neu vergeben. `vehicle` am
 * Ladepunkt trägt die Nummer des Fahrzeugs, das dort steht.
 *
 * **Zwei Grenzen, nicht eine:** Ein Katalog trägt höchstens 32 Namen, die
 * Nummern selbst reichen bis 999 — weil keine neu vergeben wird, wächst die
 * höchste mit jedem gelöschten Fahrzeug.
 */
export const WALLBOX_VEHICLE_NUMBER_MAX = 999;

export function wallboxVehicleNumberOfKey(key: string): number | null {
  const match = /^vehicle-([1-9][0-9]*)$/.exec(key);
  if (match === null) return null;
  const number = Number(match[1]);
  return number <= WALLBOX_VEHICLE_NUMBER_MAX ? number : null;
}

/**
 * Ein Wert, den die Oberfläche zeigt — und, wenn `datapointId` gesetzt und
 * `writable` wahr ist, auch setzen darf. `datapointId` ist `null`, wenn die
 * Wallbox den Datenpunkt nicht gemeldet hat: Dann gibt es nichts zu zeigen und
 * nichts zu setzen, und die Oberfläche lässt die Zeile weg.
 *
 * **`value: null` bei gesetzter Kennung** heißt „gemeldet, aber nichts, was
 * gilt": noch kein Wert, ein Code außerhalb der Tabelle, eine `0` für „nicht
 * gesetzt" (HM-165) oder ein Wert, der zu einem anderen Fahrzeug gehört.
 */
export interface WallboxField<T> {
  readonly value: T | null;
  readonly datapointId: string | null;
  readonly writable: boolean;
}

export interface WallboxVehicle {
  readonly number: number;
  readonly title: string;
}

export interface WallboxState {
  readonly mode: WallboxField<WallboxChargeMode>;
  readonly status: WallboxField<WallboxStatus>;
  readonly chargePowerW: WallboxField<number>;
  readonly energyImportWh: WallboxField<number>;
  readonly sessionEnergyWh: WallboxField<number>;
  /** Anteil 0…1, nicht Prozent. */
  readonly vehicleSoc: WallboxField<number>;
  readonly minCurrentA: WallboxField<number>;
  readonly maxCurrentA: WallboxField<number>;
  readonly phases: WallboxField<number>;
  /**
   * Das Ladeziel **dieser Sitzung**, Anteil 0…1. `0` heißt in evcc „keins —
   * es gilt das des Fahrzeugs" und wird `null` (HM-165); was tatsächlich gilt,
   * steht in `effectiveLimitSoc`.
   */
  readonly limitSoc: WallboxField<number>;
  /** Das Ladeziel, das gilt — aus Sitzung, Fahrzeug oder Plan. */
  readonly effectiveLimitSoc: WallboxField<number>;

  readonly currentL1A: WallboxField<number>;
  readonly currentL2A: WallboxField<number>;
  readonly currentL3A: WallboxField<number>;
  readonly voltageL1V: WallboxField<number>;
  readonly voltageL2V: WallboxField<number>;
  readonly voltageL3V: WallboxField<number>;
  /** Wie viele Phasen tatsächlich laden — im Gegensatz zu `phases`, der Vorgabe. */
  readonly phasesActive: WallboxField<number>;
  readonly offeredCurrentA: WallboxField<number>;
  readonly effectiveMinCurrentA: WallboxField<number>;
  readonly effectiveMaxCurrentA: WallboxField<number>;
  readonly enabled: WallboxField<boolean>;
  readonly waitingFor: WallboxField<WallboxWaitingReason>;

  readonly session: {
    readonly chargeDurationS: WallboxField<number>;
    readonly connectedDurationS: WallboxField<number>;
    /** Nur mit angestecktem Fahrzeug. */
    readonly remainingDurationS: WallboxField<number>;
    readonly remainingEnergyWh: WallboxField<number>;
    readonly solarShare: WallboxField<number>;
    /** In Euro — nur, wenn das Gateway `EUR` meldet. */
    readonly priceEur: WallboxField<number>;
    readonly pricePerKwhEur: WallboxField<number>;
    readonly co2PerKwhG: WallboxField<number>;
  };

  readonly switching: {
    readonly pvAction: WallboxField<WallboxPvAction>;
    readonly pvRemainingS: WallboxField<number>;
    readonly phaseAction: WallboxField<WallboxPhaseAction>;
    readonly phaseRemainingS: WallboxField<number>;
  };

  readonly alwaysCharge: WallboxField<WallboxAlwaysCharge>;
  /** `0` heißt „kein Energieziel" und wird `null`. */
  readonly limitEnergyWh: WallboxField<number>;
  readonly priority: WallboxField<number>;
  readonly batteryBoost: WallboxField<boolean>;
  readonly batteryBoostLimit: WallboxField<number>;
  readonly enableThresholdW: WallboxField<number>;
  readonly disableThresholdW: WallboxField<number>;
  readonly enableDelayS: WallboxField<number>;
  readonly disableDelayS: WallboxField<number>;

  readonly smartCost: WallboxSmartLimit<"EUR/kWh" | "g/kWh">;
  readonly smartFeedInPriority: WallboxSmartLimit<"EUR/kWh">;

  readonly plan: {
    /** `0` entfernt den Plan und wird `null`. */
    readonly energyWh: WallboxField<number>;
    readonly energyTime: WallboxField<Date>;
    /** Nur mit Fahrzeug. */
    readonly soc: WallboxField<number>;
    readonly socTime: WallboxField<Date>;
    readonly active: WallboxField<boolean>;
    readonly projectedStart: WallboxField<Date>;
    readonly projectedEnd: WallboxField<Date>;
    readonly overrunS: WallboxField<number>;
    /** Der Plan, der gilt — auch ein wiederkehrender aus evcc. */
    readonly effectiveSoc: WallboxField<number>;
    readonly effectiveTime: WallboxField<Date>;
  };

  readonly vehicle: {
    /** Die Nummer des Fahrzeugs am Ladepunkt; `null` = keins. */
    readonly selected: WallboxField<number>;
    /** Sein Name — `null`, wenn keins gewählt ist oder es keinen Namen gemeldet hat. */
    readonly title: string | null;
    /** Die Fahrzeuge der Anlage, nach Nummer. */
    readonly choices: readonly WallboxVehicle[];
    readonly rangeKm: WallboxField<number>;
    readonly odometerKm: WallboxField<number>;
    readonly apiLimitSoc: WallboxField<number>;
    readonly minSoc: WallboxField<number>;
    readonly limitSoc: WallboxField<number>;
  };

  /** Der jüngste Messzeitpunkt über alle Felder — `null`, solange nichts kam. */
  readonly measuredAt: Date | null;
}

/**
 * Eine Grenze für „günstig laden" oder den Einspeisevorrang (HM-165).
 *
 * **Drei Datenpunkte, eine Frage.** `limit` ist die Grenze, `limitSet` sagt, ob
 * sie gilt — denn `0` ist hier ein echter Wert und kann „nicht gesetzt" nicht
 * tragen. Fehlt `limitSet` oder ist es aus, ist `limit.value` `null`. `unit`
 * sagt, worin die Grenze gemessen wird; eine Einheit außerhalb der Verabredung
 * macht die Grenze unbekannt.
 */
export interface WallboxSmartLimit<U extends Unit> {
  readonly limit: WallboxField<number>;
  readonly unit: U | null;
  readonly limitSet: WallboxField<boolean>;
  readonly active: WallboxField<boolean>;
  readonly nextStart: WallboxField<Date>;
}

/** Was `wallboxStateOf` über einen Datenpunkt wissen muss. */
export interface WallboxDatapointSource {
  readonly id: string;
  readonly key: string;
  readonly access: DatapointAccess;
  readonly valueType: "number" | "string";
  /** Die gemeldete Einheit — gebraucht, wo eine Währung oder `smartCostType` sie trägt. */
  readonly unit?: Unit | null;
  /** Der Textwert eines Textdatenpunkts — die Namen der Fahrzeuge. */
  readonly textValue?: string | null;
}

export interface WallboxValueSource {
  readonly datapointId: string;
  readonly value: number;
  readonly measuredAt: Date;
}

const plain = (raw: number): number => raw;
/** Die 0-Regel aus HM-165: `0` heißt „nicht gesetzt". */
const unsetAtZero = (raw: number): number | null => (raw === 0 ? null : raw);
const flag = (raw: number): boolean | null => (raw === 0 ? false : raw === 1 ? true : null);
/** Unix-Sekunden UTC; `0` heißt „keiner". Kein Kalender, keine Zeitzone — ein Zeitpunkt. */
const instant = (raw: number): Date | null =>
  raw === 0 || !Number.isFinite(raw) ? null : new Date(raw * 1000);

const EMPTY: WallboxField<never> = { value: null, datapointId: null, writable: false };

/**
 * Der Zustand einer Wallbox aus ihrem Katalog und den zuletzt gemeldeten
 * Werten.
 *
 * **Gedeutet wird über den Schlüssel, nicht über die Reihenfolge**: Ein Gateway
 * darf melden, was es will; nur was so heißt wie hier verabredet, wird zu einem
 * Feld. Ein gemeldeter Datenpunkt ohne Wert ist ein Feld mit `value: null` und
 * gesetzter `datapointId` — die Oberfläche kann ihn setzen, auch wenn noch nie
 * ein Wert kam. Ein kodierter Wert außerhalb der Tabelle (ein Lademodus `7`)
 * wird `null` und nicht der nächstbeste Modus.
 *
 * **Ein Ladestand gehört zu einem angesteckten Fahrzeug** (HM-119, Nachtrag).
 * Wird das Fahrzeug abgezogen, meldet das Gateway den Ladezustand weiter jede
 * Minute, den Ladestand aber gar nicht mehr — und der letzte Wert bliebe als
 * „letzter gemeldeter" für immer stehen: 24 % an einer leeren Wallbox. Der
 * Ladestand gilt deshalb nur, wenn der Ladezustand **nicht** „kein Fahrzeug"
 * ist und der Ladestand **nicht älter** ist als die letzte Zustandsmeldung —
 * das Zweite deckt auch das nächste Fahrzeug ab, das keinen Ladestand meldet.
 * Ohne Zustandsmeldung bleibt es beim gemeldeten Wert: Dann lässt sich nichts
 * gegenhalten, und raten wäre schlimmer.
 *
 * **Dieselbe Regel gilt seit HM-165 für alles, was am Fahrzeug hängt.**
 * Reichweite, Kilometerstand, Ladegrenze im Fahrzeug und Restladezeit sendet
 * das Gateway nur angesteckt — sie folgen dem Ladezustand wie der Ladestand.
 * Mindestladestand, Ladeziel des Fahrzeugs und der Ladestandsplan gehören dem
 * **gewählten Fahrzeug**: Sie gelten nur, solange eines gewählt ist, und nur,
 * wenn sie nicht älter sind als die letzte Meldung der Fahrzeugwahl — sonst
 * stünde nach einem Wechsel das Ladeziel des vorigen Autos am neuen.
 */
export function wallboxStateOf(
  datapoints: readonly WallboxDatapointSource[],
  values: readonly WallboxValueSource[],
): WallboxState {
  const byKey = new Map(datapoints.map((point) => [point.key, point]));
  const valueById = new Map(values.map((value) => [value.datapointId, value]));

  let measuredAt: Date | null = null;
  const sampleAt = new Map<WallboxDatapointName, Date>();
  const field = <T>(
    name: WallboxDatapointName,
    decode: (raw: number) => T | null,
    units?: readonly Unit[],
  ): WallboxField<T> => {
    const point = byKey.get(WALLBOX_DATAPOINTS[name].key);
    if (point === undefined || point.valueType !== "number") return EMPTY;
    if (units !== undefined && (point.unit == null || !units.includes(point.unit))) return EMPTY;
    const sample = valueById.get(point.id);
    if (sample !== undefined) sampleAt.set(name, sample.measuredAt);
    if (sample !== undefined && (measuredAt === null || sample.measuredAt > measuredAt)) {
      measuredAt = sample.measuredAt;
    }
    return {
      value: sample === undefined ? null : decode(sample.value),
      datapointId: point.id,
      writable: isWritable(point.access),
    };
  };
  const unitOf = <U extends Unit>(name: WallboxDatapointName, allowed: readonly U[]): U | null => {
    const unit = byKey.get(WALLBOX_DATAPOINTS[name].key)?.unit;
    return unit != null && (allowed as readonly Unit[]).includes(unit) ? (unit as U) : null;
  };

  const status = field("status", wallboxStatusOf);
  const statusAt = sampleAt.get("status");
  const selected = field("vehicle", (raw) =>
    Number.isInteger(raw) && raw > 0 && raw <= WALLBOX_VEHICLE_NUMBER_MAX ? raw : null,
  );
  const selectedAt = sampleAt.get("vehicle");

  /** Gilt nur mit angestecktem Fahrzeug und nicht älter als die letzte Zustandsmeldung. */
  const plugged = <T>(name: WallboxDatapointName, decode: (raw: number) => T | null) => {
    const result = field(name, decode);
    const at = sampleAt.get(name);
    const stale =
      status.value === "disconnected" ||
      (statusAt !== undefined && at !== undefined && at < statusAt);
    return stale ? { ...result, value: null } : result;
  };
  /** Gilt nur mit gewähltem Fahrzeug und nicht älter als dessen Wahl. */
  const ofVehicle = <T>(name: WallboxDatapointName, decode: (raw: number) => T | null) => {
    const result = field(name, decode);
    const at = sampleAt.get(name);
    const stale =
      selected.value === null || (selectedAt !== undefined && at !== undefined && at < selectedAt);
    return stale ? { ...result, value: null } : result;
  };

  const smartLimit = <U extends "EUR/kWh" | "g/kWh">(
    names: {
      limit: WallboxDatapointName;
      set: WallboxDatapointName;
      active: WallboxDatapointName;
      nextStart: WallboxDatapointName;
    },
    units: readonly U[],
  ): WallboxSmartLimit<U> => {
    const limitSet = field(names.set, flag);
    const limit = field(names.limit, plain, units);
    return {
      limit: limitSet.value === true ? limit : { ...limit, value: null },
      unit: unitOf(names.limit, units),
      limitSet,
      active: field(names.active, flag),
      nextStart: field(names.nextStart, instant),
    };
  };

  const choices: WallboxVehicle[] = datapoints
    .flatMap((point) => {
      const number = wallboxVehicleNumberOfKey(point.key);
      const title = point.textValue?.trim();
      return number === null || point.valueType !== "string" || title === undefined || title === ""
        ? []
        : [{ number, title }];
    })
    .sort((a, b) => a.number - b.number);

  const state: Omit<WallboxState, "measuredAt"> = {
    mode: field("chargeMode", wallboxChargeModeOf),
    status,
    chargePowerW: field("chargePower", plain),
    energyImportWh: field("energyImport", plain),
    sessionEnergyWh: field("sessionEnergy", plain),
    vehicleSoc: plugged("vehicleSoc", plain),
    minCurrentA: field("minCurrent", plain),
    maxCurrentA: field("maxCurrent", plain),
    phases: field("phases", (raw) =>
      (WALLBOX_PHASE_CHOICES as readonly number[]).includes(raw) ? raw : null,
    ),
    limitSoc: field("limitSoc", unsetAtZero),
    effectiveLimitSoc: field("effectiveLimitSoc", unsetAtZero),

    currentL1A: field("currentL1", plain),
    currentL2A: field("currentL2", plain),
    currentL3A: field("currentL3", plain),
    voltageL1V: field("voltageL1", plain),
    voltageL2V: field("voltageL2", plain),
    voltageL3V: field("voltageL3", plain),
    phasesActive: field("phasesActive", (raw) => ([1, 2, 3].includes(raw) ? raw : null)),
    offeredCurrentA: field("offeredCurrent", plain),
    effectiveMinCurrentA: field("effectiveMinCurrent", plain),
    effectiveMaxCurrentA: field("effectiveMaxCurrent", plain),
    enabled: field("enabled", flag),
    waitingFor: field("chargerStatusReason", wallboxWaitingReasonOf),

    session: {
      chargeDurationS: field("chargeDuration", plain),
      connectedDurationS: plugged("connectedDuration", plain),
      remainingDurationS: plugged("chargeRemainingDuration", plain),
      remainingEnergyWh: plugged("chargeRemainingEnergy", plain),
      solarShare: field("sessionSolarShare", plain),
      priceEur: field("sessionPrice", plain, ["EUR"]),
      pricePerKwhEur: field("sessionPricePerKwh", plain, ["EUR/kWh"]),
      co2PerKwhG: field("sessionCo2PerKwh", plain, ["g/kWh"]),
    },

    switching: {
      pvAction: field("pvAction", wallboxPvActionOf),
      pvRemainingS: field("pvRemaining", plain),
      phaseAction: field("phaseAction", wallboxPhaseActionOf),
      phaseRemainingS: field("phaseRemaining", plain),
    },

    alwaysCharge: field("alwaysCharge", wallboxAlwaysChargeOf),
    limitEnergyWh: field("limitEnergy", unsetAtZero),
    priority: field("priority", plain),
    batteryBoost: field("batteryBoost", flag),
    batteryBoostLimit: field("batteryBoostLimit", plain),
    enableThresholdW: field("enableThreshold", plain),
    disableThresholdW: field("disableThreshold", plain),
    enableDelayS: field("enableDelay", plain),
    disableDelayS: field("disableDelay", plain),

    smartCost: smartLimit(
      {
        limit: "smartCostLimit",
        set: "smartCostLimitSet",
        active: "smartCostActive",
        nextStart: "smartCostNextStart",
      },
      ["EUR/kWh", "g/kWh"],
    ),
    smartFeedInPriority: smartLimit(
      {
        limit: "smartFeedInPriorityLimit",
        set: "smartFeedInPriorityLimitSet",
        active: "smartFeedInPriorityActive",
        nextStart: "smartFeedInPriorityNextStart",
      },
      ["EUR/kWh"],
    ),

    plan: {
      energyWh: field("planEnergy", unsetAtZero),
      energyTime: field("planEnergyTime", instant),
      soc: ofVehicle("planSoc", unsetAtZero),
      socTime: ofVehicle("planSocTime", instant),
      active: field("planActive", flag),
      projectedStart: field("planProjectedStart", instant),
      projectedEnd: field("planProjectedEnd", instant),
      overrunS: field("planOverrun", plain),
      effectiveSoc: field("effectivePlanSoc", unsetAtZero),
      effectiveTime: field("effectivePlanTime", instant),
    },

    vehicle: {
      selected,
      title:
        selected.value === null
          ? null
          : (choices.find((choice) => choice.number === selected.value)?.title ?? null),
      choices,
      rangeKm: plugged("vehicleRange", unsetAtZero),
      odometerKm: plugged("vehicleOdometer", unsetAtZero),
      apiLimitSoc: plugged("vehicleApiLimitSoc", unsetAtZero),
      minSoc: ofVehicle("vehicleMinSoc", plain),
      limitSoc: ofVehicle("vehicleLimitSoc", plain),
    },
  };

  return { ...state, measuredAt };
}

/** Unix-Sekunden, ab denen ein Planzeitpunkt plausibel ist (2001-09-09). */
const PLAN_TIME_MIN = 1_000_000_000;

const between = (min: number, max: number, message: string) => (value: number) =>
  value >= min && value <= max ? null : message;
const integerBetween = (min: number, max: number, message: string) => (value: number) =>
  Number.isInteger(value) && value >= min && value <= max ? null : message;
const oneOf = (allowed: readonly number[], message: string) => (value: number) =>
  allowed.includes(value) ? null : message;

const RATIO = between(0, 1, "Der Wert ist ein Anteil zwischen 0 und 1.");
const SWITCH = oneOf([0, 1], "Der Schalter ist 0 (aus) oder 1 (an).");
const UNSET_ONLY = oneOf(
  [0],
  "Eingerichtet wird eine Grenze mit ihrem Wert; hier lässt sie sich nur mit 0 entfernen.",
);
const PLAN_TIME = (value: number) =>
  value === 0 || (Number.isInteger(value) && value >= PLAN_TIME_MIN)
    ? null
    : "Der Zeitpunkt ist Unix-Sekunden (UTC) oder 0, um den Plan zu entfernen.";
const PRICE_LIMIT = between(-10_000, 10_000, "Die Grenze muss zwischen −10 000 und 10 000 liegen.");
const THRESHOLD = between(
  -100_000,
  100_000,
  "Die Schwelle muss zwischen −100 000 und 100 000 W liegen.",
);
const DELAY = integerBetween(0, 86_400, "Die Verzögerung ist eine ganze Zahl von 0 bis 86 400 s.");
const ENERGY_TARGET = between(
  0,
  1_000_000,
  "Das Energieziel muss zwischen 0 und 1 000 000 Wh liegen.",
);

/**
 * Die Wertebereiche eines Auftrags je Schlüssel — dieselben wie im Gateway
 * (`internal/core/wallbox/extended.go`, `checkProposed`), damit ein Auftrag
 * nicht erst nach der Wartezeit mit `invalid` zurückkommt.
 */
const WRITE_CHECKS: Readonly<Record<string, (value: number) => string | null>> = {
  [WALLBOX_DATAPOINTS.chargeMode.key]: (value) =>
    wallboxChargeModeOf(value) === null
      ? "Der Lademodus muss 0 (aus), 1 (PV), 2 (Min+PV) oder 3 (sofort) sein."
      : null,
  [WALLBOX_DATAPOINTS.phases.key]: oneOf(
    WALLBOX_PHASE_CHOICES,
    "Die Phasenzahl muss 0 (automatisch), 1 oder 3 sein.",
  ),
  [WALLBOX_DATAPOINTS.minCurrent.key]: between(0, 80, "Der Strom muss zwischen 0 und 80 A liegen."),
  [WALLBOX_DATAPOINTS.maxCurrent.key]: between(0, 80, "Der Strom muss zwischen 0 und 80 A liegen."),
  [WALLBOX_DATAPOINTS.limitSoc.key]: between(0, 1, "Das Ladeziel ist ein Anteil zwischen 0 und 1."),
  [WALLBOX_DATAPOINTS.alwaysCharge.key]: (value) =>
    wallboxAlwaysChargeOf(value) === null
      ? "„Immer mit Mindestleistung“ ist 0 (aus), 1 (an) oder 2 (bis zum Abstecken)."
      : null,
  [WALLBOX_DATAPOINTS.limitEnergy.key]: ENERGY_TARGET,
  [WALLBOX_DATAPOINTS.priority.key]: integerBetween(
    0,
    10,
    "Der Vorrang ist eine ganze Zahl von 0 bis 10.",
  ),
  [WALLBOX_DATAPOINTS.batteryBoost.key]: SWITCH,
  [WALLBOX_DATAPOINTS.batteryBoostLimit.key]: RATIO,
  [WALLBOX_DATAPOINTS.enableThreshold.key]: THRESHOLD,
  [WALLBOX_DATAPOINTS.disableThreshold.key]: THRESHOLD,
  [WALLBOX_DATAPOINTS.enableDelay.key]: DELAY,
  [WALLBOX_DATAPOINTS.disableDelay.key]: DELAY,
  [WALLBOX_DATAPOINTS.smartCostLimit.key]: PRICE_LIMIT,
  [WALLBOX_DATAPOINTS.smartCostLimitSet.key]: UNSET_ONLY,
  [WALLBOX_DATAPOINTS.smartFeedInPriorityLimit.key]: PRICE_LIMIT,
  [WALLBOX_DATAPOINTS.smartFeedInPriorityLimitSet.key]: UNSET_ONLY,
  [WALLBOX_DATAPOINTS.planEnergy.key]: ENERGY_TARGET,
  [WALLBOX_DATAPOINTS.planEnergyTime.key]: PLAN_TIME,
  [WALLBOX_DATAPOINTS.planSoc.key]: RATIO,
  [WALLBOX_DATAPOINTS.planSocTime.key]: PLAN_TIME,
  [WALLBOX_DATAPOINTS.vehicle.key]: integerBetween(
    0,
    WALLBOX_VEHICLE_NUMBER_MAX,
    "Das Fahrzeug ist 0 (keins) oder seine Nummer aus `vehicle-{n}`.",
  ),
  [WALLBOX_DATAPOINTS.vehicleMinSoc.key]: RATIO,
  [WALLBOX_DATAPOINTS.vehicleLimitSoc.key]: RATIO,
};

/**
 * Ob ein Wert an diesen Datenpunkt der Wallbox geschrieben werden darf — die
 * Prüfung **vor** dem Senden, damit ein Auftrag nicht mit `invalid` zurückkommt,
 * was der Nutzer erst nach der Wartezeit erführe. **Ein Schutz ist es nicht**;
 * der liegt beim Gateway und bei EVCC.
 *
 * Für einen Schlüssel, der hier nicht steht, gibt es keine Regel — und damit
 * keine Ablehnung: Was die Wallbox als schreibbar meldet und die Cloud nicht
 * kennt, darf sie setzen; nur die verabredeten Schlüssel haben Grenzen.
 */
export function wallboxWriteProblem(key: string, value: number): string | null {
  return WRITE_CHECKS[key]?.(value) ?? null;
}
